import { SxProps } from '@mui/system';
import { Box, Grid, useTheme } from '@mui/material';
import AppCustomIcon from '@/app/features/icon/AppIcon';
import { ReactNode } from 'react';
import React from 'react';
import AppFormatAmount from '@/app/features/format/AppFormatAmount';
import CAppPaper from '@/app/components/CAppPaper';
import { AppBackgroundTriangleBgDarkBlueSrc } from '@/app/features/background/AppBackground';
import CAppInfoTooltip from '@/app/components/CAppInfoTooltip';
import langInvestWordLang from '@/apps/investor/langs/langInvestWordLang';
import { TInvestFundCurrency } from '@/apps/investor/types/fund/TInvestFundCurrency';
import { useTranslation } from 'react-i18next';
import styleVariables from '@/app/styles/styleVariables';

type CCardStatsProps = {
	title: string;
	rows: {
		label: string;
		tooltip?: string;
		value: number;
		currency: TInvestFundCurrency;
	}[];
};
const CCardStatsValues = (props: CCardStatsProps) => {
	const { title, rows } = props;
	const { t } = useTranslation();

	const theme = useTheme();

	const styleTitle = {
		color: theme.palette.secondary.main,
		verticalAlign: 'text-bottom',
		boxSizing: 'inherit',
		textAlign: 'left',
		alignItems: 'center',
		display: 'inline-flex',
		fontWeight: '500',
		fontSize: '1.25rem',
		textTransform: 'uppercase',
		'& .icon': {
			marginRight: '10px',
		},
	};
	const styleKPI = {
		display: 'flex',
		flexDirection: 'column',
		padding: '30px',
		background: `url(${AppBackgroundTriangleBgDarkBlueSrc}), ${theme.palette.primary.main}`,
		backgroundSize: 'auto 100%',
		backgroundRepeat: 'no-repeat',
		backgroundPosition: 'top right',
		border: '0px',
		borderRadius: '10px',
		textAlign: 'left',
		'& .title': styleTitle,
		'& .list': {
			color: 'white',
			textAlign: 'left',
			alignItems: 'center',
			padding: '10px 0px 0px 0px',
			fontWeight: 'normal',
			// Custom vertical scroll in y overflow
			overflowY: 'auto',
			'&::-webkit-scrollbar': {
				width: '0.5rem',
			},
			'&::-webkit-scrollbar-track': {
				background: styleVariables.primaryColor,
			},
			'&::-webkit-scrollbar-thumb': {
				background: styleVariables.secondaryColor,
			},
			'&::-webkit-scrollbar-thumb:hover': {
				background: styleVariables.greyColor,
			},
			'&::-webkit-scrollbar-thumb:vertical': {
				borderRadius: '0.5rem',
			},
			'&::-webkit-scrollbar-thumb:horizontal': {
				borderRadius: '0.5rem',
			},
			// Push scroll bar to the right 10px
			marginRight: '-10px',
			paddingRight: '15px',

			'& .item': {
				color: 'white',
				textAlign: 'left',
				alignItems: 'center',
				padding: '6px 0px',
				fontWeight: 'normal',
				'& .value': {
					textAlign: 'right',
					fontWeight: '600',
					fontSize: '1.25rem',
				},
				'& .label': {
					textAlign: 'left',
				},
			},
			'& .item:last-child': {
				paddingBottom: '0px',
			},
		},
	} as SxProps;

	return (
		<CAppPaper
			id="expertise-capital-kpi-component"
			color="primary"
			displayBackgroundImage
			variant="outlined"
			sx={styleKPI}
		>
			<Box className="title" sx={{ flex: '10%' }}>
				<AppCustomIcon tag="pie_chart" className="icon" /> {title}
			</Box>
			<Box className="list" sx={{ flex: '90%' }}>
				{rows.map((row, index) => (
					<CCardStatsRow
						key={index}
						label={row.label}
						tooltip={row.tooltip}
						value={
							<AppFormatAmount
								value={row.value}
								addSpace
								textIfNull={t(langInvestWordLang.nc.tag)}
								currency={row.currency}
							/>
						}
					/>
				))}
			</Box>
		</CAppPaper>
	);
};

type CCardStatsRowProps = {
	label: string | ReactNode;
	tooltip?: string;
	value: string | ReactNode;
};
const CCardStatsRow = (props: CCardStatsRowProps) => {
	const { label, tooltip, value } = props;

	return (
		<Box className="item">
			<Grid container spacing={3}>
				<Grid item sm={8} xs={7} className="label">
					{label}
					{tooltip && <CAppInfoTooltip title={tooltip} />}
				</Grid>
				<Grid item sm={4} xs={5} className="value">
					{value}
				</Grid>
			</Grid>
		</Box>
	);
};

export { CCardStatsValues };
